/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import * as React from "react";
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled, { css } from "styled-components";
import { ReactComponent as ChevronDownIcon } from "../images/icons/chevron-down.svg";
import { ReactComponent as ChevronUpIcon } from "../images/icons/chevron-up.svg";
import { ReactComponent as CheckIcon } from "../images/icons/floating-check_2px.svg";
import { Tooltip, Typography14 } from "@slid/slid-ips";

type DropDownContextType = {
  registerItem: (ref: React.RefObject<HTMLButtonElement>) => void;
};

const DropDownContext = React.createContext<DropDownContextType | null>(null);

const dropDownPadding = 6;

export function DropDownItem({
  children,
  className,
  onClick,
  title,
  isActive = false,
}: {
  children: React.ReactNode;
  className: string;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  title?: string;
  isActive?: boolean;
}) {
  const ref = useRef<HTMLButtonElement>(null);

  const dropDownContext = React.useContext(DropDownContext);

  if (dropDownContext === null) {
    throw new Error("DropDownItem must be used within a DropDown");
  }

  const { registerItem } = dropDownContext;

  useEffect(() => {
    if (ref && ref.current) {
      registerItem(ref);
    }
  }, [ref, registerItem]);

  return (
    <DropdownItemButton className={className} onClick={onClick} ref={ref} title={title} type="button">
      <ItemWrapper>{children}</ItemWrapper>
      {isActive && <CheckIcon />}
    </DropdownItemButton>
  );
}

const ItemWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

function DropDownItems({ children, dropDownRef, onClose, maxHeight }: { children: React.ReactNode; dropDownRef: React.Ref<HTMLDivElement>; onClose: () => void; maxHeight?: number }) {
  const [items, setItems] = useState<React.RefObject<HTMLButtonElement>[]>();
  const [highlightedItem, setHighlightedItem] = useState<React.RefObject<HTMLButtonElement>>();

  const registerItem = useCallback(
    (itemRef: React.RefObject<HTMLButtonElement>) => {
      setItems((prev) => (prev ? [...prev, itemRef] : [itemRef]));
    },
    [setItems]
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!items) {
      return;
    }

    const key = event.key;

    if (["Escape", "ArrowUp", "ArrowDown", "Tab"].includes(key)) {
      event.preventDefault();
    }

    if (key === "Escape" || key === "Tab") {
      onClose();
    } else if (key === "ArrowUp") {
      setHighlightedItem((prev) => {
        if (!prev) {
          return items[0];
        }
        const index = items.indexOf(prev) - 1;
        return items[index === -1 ? items.length - 1 : index];
      });
    } else if (key === "ArrowDown") {
      setHighlightedItem((prev) => {
        if (!prev) {
          return items[0];
        }
        return items[items.indexOf(prev) + 1];
      });
    }
  };

  const contextValue = useMemo(
    () => ({
      registerItem,
    }),
    [registerItem]
  );

  useEffect(() => {
    if (items && !highlightedItem) {
      setHighlightedItem(items[0]);
    }

    if (highlightedItem && highlightedItem.current) {
      highlightedItem.current.focus();
    }
  }, [items, highlightedItem]);

  return (
    <DropDownContext.Provider value={contextValue}>
      <Container className="editor-dropdown" ref={dropDownRef} onKeyDown={handleKeyDown} maxHeight={maxHeight}>
        {children}
      </Container>
    </DropDownContext.Provider>
  );
}

export default function DropDown({
  disabled = false,
  buttonLabel,
  buttonLabelIcon,
  buttonAriaLabel,
  buttonClassName,
  buttonIconClassName,
  children,
  stopCloseOnClickSelf,
  showChevron = true,
  isFloatingToolbar = false,
  tooltipTitle,
  onClose,
  onOpen,
  maxHeight,
  style,
}: {
  disabled?: boolean;
  buttonAriaLabel?: string;
  buttonClassName: string;
  buttonIconClassName?: string;
  buttonLabel?: string;
  buttonLabelIcon?: ReactNode;
  children: ReactNode;
  stopCloseOnClickSelf?: boolean;
  showChevron?: boolean;
  isFloatingToolbar?: boolean;
  tooltipTitle?: string;
  onClose?: () => void;
  onOpen?: () => void;
  maxHeight?: number;
  style?: React.CSSProperties;
}): JSX.Element {
  const dropDownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [showDropDown, setShowDropDown] = useState(false);

  const handleClose = () => {
    setShowDropDown(false);
    if (buttonRef && buttonRef.current) {
      buttonRef.current.focus();
    }
  };

  useEffect(() => {
    if (showDropDown) {
      onOpen?.();
    } else {
      onClose?.();
    }
  }, [showDropDown]);

  useEffect(() => {
    const updateDropDownPosition = () => {
      const button = buttonRef.current;
      const dropDown = dropDownRef.current;

      if (showDropDown && button !== null && dropDown !== null) {
        const { top, left } = button.getBoundingClientRect();
        dropDown.style.top = `${top + button.offsetHeight + dropDownPadding}px`;
        dropDown.style.left = `${Math.min(left, window.innerWidth - dropDown.offsetWidth - 20)}px`;
      }
    };

    updateDropDownPosition();

    // NOTE: to make dropdown move together with the floating toolbar while scrolling and resizing
    window.addEventListener("scroll", updateDropDownPosition, true);
    window.addEventListener("resize", updateDropDownPosition);

    return () => {
      window.removeEventListener("scroll", updateDropDownPosition, true);
      window.removeEventListener("resize", updateDropDownPosition);
    };
  }, [dropDownRef, buttonRef, showDropDown]);

  useEffect(() => {
    const button = buttonRef.current;

    if (button !== null && showDropDown) {
      const handle = (event: MouseEvent) => {
        const target = event.target;
        if (stopCloseOnClickSelf) {
          if (dropDownRef.current && dropDownRef.current.contains(target as Node)) {
            return;
          }
        }
        if (!button.contains(target as Node)) {
          setShowDropDown(false);
        }
      };
      document.addEventListener("click", handle);

      return () => {
        document.removeEventListener("click", handle);
      };
    }
  }, [dropDownRef, buttonRef, showDropDown, stopCloseOnClickSelf]);

  useEffect(() => {
    const handleButtonPositionUpdate = () => {
      if (showDropDown) {
        const button = buttonRef.current;
        const dropDown = dropDownRef.current;
        if (button !== null && dropDown !== null) {
          const { top } = button.getBoundingClientRect();
          const newPosition = top + button.offsetHeight + dropDownPadding;
          if (newPosition !== dropDown.getBoundingClientRect().top) {
            dropDown.style.top = `${newPosition}px`;
          }
        }
      }
    };

    document.addEventListener("scroll", handleButtonPositionUpdate);

    return () => {
      document.removeEventListener("scroll", handleButtonPositionUpdate);
    };
  }, [buttonRef, dropDownRef, showDropDown]);

  const renderDropdownButton = () => {
    return (
      <DropdownButton
        type="button"
        disabled={disabled}
        aria-label={buttonAriaLabel || buttonLabel}
        className={buttonClassName + (isFloatingToolbar ? " floating-toolbar-button" : "")}
        onClick={() => {
          setShowDropDown(!showDropDown);
        }}
        ref={buttonRef}
        style={style}
      >
        {buttonIconClassName && <span className={buttonIconClassName} />}
        {buttonLabelIcon && <DropdownIconWrapper>{buttonLabelIcon}</DropdownIconWrapper>}
        {buttonLabel && <Typography14 text={buttonLabel} color="--gray10" style={{ marginRight: "8px", marginLeft: "6px" }} />}
        {showChevron && <ChevronIconWrapper>{showDropDown ? <ChevronUpIcon /> : <ChevronDownIcon />}</ChevronIconWrapper>}
      </DropdownButton>
    );
  };

  return (
    <>
      {tooltipTitle ? (
        <Tooltip title={tooltipTitle} placement={isFloatingToolbar ? "top" : "bottom"}>
          {renderDropdownButton()}
        </Tooltip>
      ) : (
        renderDropdownButton()
      )}

      {showDropDown &&
        createPortal(
          <DropDownItems dropDownRef={dropDownRef} onClose={handleClose} maxHeight={maxHeight}>
            {children}
          </DropDownItems>,
          document.body
        )}
    </>
  );
}

const DropdownItemButton = styled.button`
  /* s of reset button */
  background: inherit;
  border: none;
  box-shadow: none;
  -webkit-tap-highlight-color: transparent;
  box-sizing: border-box;
  outline-style: none;

  &:focus {
    outline: 0;
    border: none;
  }
  /* e of reset button  */

  display: flex;
  padding: 8px 16px;
  align-items: center;
  gap: 4px;
  align-self: stretch;
  cursor: pointer;
  width: 100%;
  background-color: transparent;
`;

const Container = styled.div<{ maxHeight?: number }>`
  z-index: 0;
  display: block;
  position: fixed;
  border-radius: 8px;
  box-shadow: var(--boxShadow2);
  min-height: 40px;
  background-color: #fff;
  padding: 8px 0;
  max-height: ${(props) => props.maxHeight}px;
  ${({ maxHeight }) =>
    maxHeight &&
    css`
      overflow-y: auto;
      &::-webkit-scrollbar {
        opacity: 0;
        background: transparent;
        width: 6px;
      }
      &::-webkit-scrollbar-thumb {
        background: transparent;
        border-radius: 6px;
      }
      &::-webkit-scrollbar:hover {
        opacity: 1;
      }
      &:hover::-webkit-scrollbar-thumb {
        background: rgba(100, 100, 100, 0.5);
      }
    `}
`;

const DropdownButton = styled.button`
  /* s of reset button */
  background: inherit;
  border: none;
  box-shadow: none;
  -webkit-tap-highlight-color: transparent;
  box-sizing: border-box;
  outline-style: none;
  padding: 0;
  margin: 0;

  &:focus {
    outline: 0;
    border: none;
  }
  /* e of reset button  */

  display: flex;
  align-items: center;
  justify-content: center;
  height: 32px;

  /* NOTE: prevent click not working on chevron icon */
  svg {
    pointer-events: none;
  }
`;

const DropdownIconWrapper = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ChevronIconWrapper = styled.div`
  padding-right: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
